import { Box, Flex, IconButton, Slide, Stack, Text } from '@chakra-ui/react'
import {
  faBell,
  faBellSlash,
  faClose,
  faEllipsis,
  faEnvelopeOpen,
  faGear,
} from '@fortawesome/pro-regular-svg-icons'
import { useCallback, useRef } from 'react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'
import { isNotNull } from '@/utils/helpers'

import { useUser } from '@/client/context/user'
import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import RPC from '@/client/rpc'
import { PlatformRuntime, usePushNotifications } from '@/client/services/platform'
import { getSettingsUrl } from '@/client/services/url'

import PlatformHeaderBase from '@/components/Layout/PlatformHeaderBase'
import Icon from '@/components/UI/Icon/Icon'
import Menu from '@/components/Worlds/UI/Menu/Menu'

import NotificationItems from './NotificationItems'

const NOTIFICATION_DRAWER_WIDTH = '400px'

export default function NotificationDrawer({
  isOpen,
  onClose,
}: {
  isOpen: boolean
  onClose: () => void
}): JSX.Element | null {
  const {
    isSupported: browserSupportsWebPush,
    isEnabled: subscriptionEnabled,
    toggleSubscription,
  } = usePushNotifications()
  const user = useUser()
  const drawerRef = useRef<HTMLDivElement>(null)

  const [onMarkAllAsRead] = useAPIWrapper(
    async () => {
      await RPC.markAllNotificationsAsClicked({})
    },
    [],
    'Mark all notifications as clicked'
  )

  const platformLabel = user.platformRuntime === PlatformRuntime.web ? 'browser' : 'device'

  const toggleWebPushMenuItem = browserSupportsWebPush
    ? {
        label: subscriptionEnabled
          ? `Disable notifications on this ${platformLabel}`
          : `Enable notifications on this ${platformLabel}`,
        onClick: toggleSubscription,
        icon: subscriptionEnabled ? faBellSlash : faBell,
      }
    : null

  const menuItems = [
    { icon: faEnvelopeOpen, label: 'Mark all as read', onClick: onMarkAllAsRead },
    toggleWebPushMenuItem,
    { icon: faGear, label: 'Notification settings', href: getSettingsUrl('notifications') },
  ].filter(isNotNull)

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (drawerRef.current == null) {
        return
      }
      if (!drawerRef.current.contains(e.target as Node)) {
        onClose()
      }
    },
    [drawerRef, onClose]
  )

  return (
    <Slide in={isOpen} direction="left" style={{ zIndex: 3 }} onClick={handleClick}>
      <Flex
        ref={drawerRef}
        direction="column"
        top={0}
        height="100vh"
        bg="bg"
        maxWidth={NOTIFICATION_DRAWER_WIDTH}
        boxShadow={{ base: 'none', sm: `6px 0px 6px 1px ${chakraTokenToVar('border.muted')}` }}
      >
        <PlatformHeaderBase
          leftContent={<Text>Activity</Text>}
          rightContent={
            <Stack direction="row">
              <Menu
                buttonProps={{
                  'aria-label': 'Notification Actions',
                  icon: <Icon faIcon={faEllipsis} size="md" color="fg.muted" />,
                }}
                itemGroups={[menuItems]}
              />
              <IconButton
                variant="icon"
                cursor="pointer"
                icon={<Icon size="md" faIcon={faClose} />}
                aria-label="Close notifications"
                onClick={onClose}
              />
            </Stack>
          }
        />
        <Box overflowY="auto" width="100%" flex={1} padding="16px">
          <NotificationItems isOpen={isOpen} />
        </Box>
      </Flex>
    </Slide>
  )
}
